import {
  DollarSign,
  Calendar,
  Package,
  ClipboardList,
} from 'lucide-react';
import type {
  PurchaseOrder, POStatus 
} from '../../types';

interface DetailsTabProps {readonly po: PurchaseOrder;}

export function DetailsTab({ po }: DetailsTabProps) {
  return (
    <div className="space-y-6">
      <SummaryCards po={po} />
      <OrderInformation po={po} />
      <FinancialSummary po={po} />
      {po.notes && <NotesSection notes={po.notes} />}
      <NextStepsSection status={po.status} />
    </div>
  );
}

function SummaryCards({ po }: { readonly po: PurchaseOrder }) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <DollarSign className="h-4 w-4" />
          Total Value
        </div>
        <p className="mt-1 text-2xl font-bold text-gray-900">
          ${po.total.toLocaleString()}{' '}
          <span className="text-sm font-normal text-gray-500">{po.currency}</span>
        </p>
      </div>
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Package className="h-4 w-4" />
          Line Items
        </div>
        <p className="mt-1 text-2xl font-bold text-gray-900">{po.lines.length}</p>
      </div>
      <div className="rounded-lg border bg-gray-50 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar className="h-4 w-4" />
          Expected Delivery
        </div>
        <p className="mt-1 text-2xl font-bold text-gray-900">
          {new Date(po.expectedDeliveryDate).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

function OrderInformation({ po }: { readonly po: PurchaseOrder }) {
  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <h3 className="font-medium text-gray-900">Order Information</h3>
      </div>
      <div className="grid gap-4 p-4 md:grid-cols-2">
        <div>
          <p className="text-sm text-gray-500">Supplier</p>
          <p className="font-medium text-gray-900">{po.supplierName}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Delivery Site</p>
          <p className="font-medium text-gray-900">{po.deliverySiteName}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Delivery Zone</p>
          <p className="font-medium text-gray-900">{po.deliveryZone}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Created By</p>
          <p className="font-medium text-gray-900">{po.createdByName}</p>
        </div>
        {po.approvedByName && (
          <div>
            <p className="text-sm text-gray-500">Approved By</p>
            <p className="font-medium text-gray-900">{po.approvedByName}</p>
          </div>
        )}
        <div>
          <p className="text-sm text-gray-500">Created At</p>
          <p className="font-medium text-gray-900">
            {new Date(po.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

function FinancialSummary({ po }: { readonly po: PurchaseOrder }) {
  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <h3 className="font-medium text-gray-900">Financial Summary</h3>
      </div>
      <div className="p-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Subtotal</span>
            <span className="text-gray-900">${po.subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Tax</span>
            <span className="text-gray-900">${po.tax.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-t pt-2 text-base font-medium">
            <span className="text-gray-900">Total</span>
            <span className="text-gray-900">
              ${po.total.toLocaleString()} {po.currency}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotesSection({ notes }: { readonly notes: string }) {
  return (
    <div className="rounded-lg border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <h3 className="font-medium text-gray-900">Notes</h3>
      </div>
      <div className="p-4">
        <p className="text-gray-700">{notes}</p>
      </div>
    </div>
  );
}

function getNextSteps(status: POStatus): string[] {
  switch (status) {
    case 'draft':
      return ['Review all line items for accuracy', 'Submit for approval when ready'];
    case 'pending':
      return ['Awaiting manager approval', 'Contact purchasing manager if urgent'];
    case 'approved':
      return ['Send PO to supplier', 'Confirm supplier contact information'];
    case 'sent':
      return [
        'Wait for supplier acknowledgment',
        'Follow up if no response within 24 hours',
      ];
    case 'acknowledged':
      return ['Monitor shipment status', 'Prepare receiving area for delivery'];
    case 'shipped':
      return [
        'Track delivery progress',
        'Notify receiving team of expected arrival',
        'Prepare prep list for incoming items',
      ];
    case 'delivered':
      return [
        'Complete receiving inspection',
        'Report any discrepancies',
        'Update inventory records',
      ];
    default:
      return [];
  }
}

function NextStepsSection({ status }: { readonly status: POStatus }) {
  const nextSteps = getNextSteps(status);

  if (nextSteps.length === 0) return null;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <h3 className="flex items-center gap-2 font-medium text-blue-900">
        <ClipboardList className="h-5 w-5" />
        Next Steps
      </h3>
      <ul className="mt-3 space-y-2">
        {nextSteps.map((step) => (
          <li
            key={step}
            className="flex items-start gap-2 text-sm text-blue-800"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs font-medium text-blue-900">
              {nextSteps.indexOf(step) + 1}
            </span>
            {step}
          </li>
        ))}
      </ul>
    </div>
  );
}
